import { fetchVhodyashchie, type VhodyashcheeUvedomlenie } from '../obrashcheniya/api';

/**
 * ОДНО УВЕДОМЛЕНИЕ И ОДИН ЗВУК НА ВСЕ ВКЛАДКИ (`s720`, приёмка 06.10.2026).
 *
 * У дежурного панель открыта в нескольких вкладках. Опрашивай ленту каждая — входящее всплыло бы в
 * каждой и прозвучало бы столько раз, сколько вкладок, а серверу досталось бы столько же запросов.
 *
 *  · ВЕДУЩАЯ ВКЛАДКА ОДНА. Её выбирает замок браузера (`navigator.locks`): кто его держит, тот
 *    опрашивает ленту и раздаёт новое остальным (`BroadcastChannel`). Закрыли ведущую — замок
 *    свободен, его берёт следующая; отметку ленты она продолжает с общей (`localStorage`).
 *  · КАРТОЧКУ ДИАЛОГА ПОКАЗЫВАЕТ ОДНА ВКЛАДКА: та, что на виду и первой взяла замок этого
 *    диалога. Вкладка не на виду ждёт: станет видимой — покажет то, что ещё свежо и нигде не показано.
 *  · ДИАЛОГ, ОТКРЫТЫЙ НА ВИДУ в какой-то вкладке, не всплывает нигде и не звучит: эта вкладка
 *    говорит остальным «вижу».
 *  · ЗВУК ДАЁТ ВЕДУЩАЯ, один раз на пачку, если в пачке есть хоть один диалог, который никто не видит.
 *
 * Браузер без замков или без канала вкладок (старые) работает по-старому: каждая вкладка сама
 * себе ведущая — уведомление будет, но в каждой вкладке своё.
 */
export type Poslanie =
  | { tip: 'novye'; soobshcheniya: VhodyashcheeUvedomlenie[]; slova: Record<string, string> }
  | { tip: 'vidno'; obrashchenieId: number }
  | { tip: 'pokazano'; ids: number[] };

const KANAL = 'opsmind-uvedomleniya-v1';
const ZAMOK_VEDUSHCHEY = 'opsmind-uved-vedushchiy';
const KLYUCH_KURSORA = 'opsmind.uved.kursor';
/** Сколько ждём слово «вижу» от вкладки с открытым диалогом, прежде чем показывать и звучать. */
export const ZHDAT_VIDNO_MS = 150;

const zamki = (): LockManager | null => (typeof navigator !== 'undefined' && 'locks' in navigator ? navigator.locks : null);

function kursorIzPamyati(): number | null {
  try {
    const n = Number(window.localStorage.getItem(KLYUCH_KURSORA));

    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function zapomnitKursor(k: number): void {
  try {
    window.localStorage.setItem(KLYUCH_KURSORA, String(k));
  } catch {
    // Без общей отметки новая ведущая начнёт с «сейчас» — пропустит только секунды передачи.
  }
}

export interface Svyaz {
  /** Сказать остальным вкладкам. Себе послание не приходит. */
  skazat: (p: Poslanie) => void;
  ostanovit: () => void;
}

/**
 * Включить вкладку в общую работу. `onPoslanie` получает послания соседей И собственные «новые»
 * ведущей — вкладке всё равно, кто принёс сообщение. `onPachka` зовётся только у ведущей.
 */
export function podklyuchit(
  onPoslanie: (p: Poslanie) => void,
  onPachka: (soobshcheniya: VhodyashcheeUvedomlenie[]) => void,
): Svyaz {
  let zhiva = true;
  let taymer: number | undefined;
  let otpustitVedushchuyu: (() => void) | null = null;
  const kanal = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(KANAL) : null;

  if (kanal !== null) kanal.onmessage = (e: MessageEvent<Poslanie>) => { if (zhiva) onPoslanie(e.data); };

  const skazat = (p: Poslanie) => {
    try {
      kanal?.postMessage(p);
    } catch {
      // Канал закрыт (вкладка уходит) — молчим.
    }
  };

  const oprashivat = () => {
    let kursor = kursorIzPamyati();

    const shag = async () => {
      if (!zhiva) return;
      let pauza: number;

      try {
        const otvet = await fetchVhodyashchie(kursor);
        if (!zhiva) return;
        kursor = otvet.kursor;
        zapomnitKursor(otvet.kursor);
        pauza = Math.max(2, otvet.opros_sek ?? 5) * 1000;

        if (otvet.soobshcheniya.length > 0) {
          const poslanie: Poslanie = { tip: 'novye', soobshcheniya: otvet.soobshcheniya, slova: otvet.slova ?? {} };
          skazat(poslanie);
          onPoslanie(poslanie);
          onPachka(otvet.soobshcheniya);
        } else if (otvet.slova !== undefined) {
          // Слова уведомления нужны и до первого входящего (настройки, подписи) — отдаём пустой пачкой.
          onPoslanie({ tip: 'novye', soobshcheniya: [], slova: otvet.slova });
        }
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        // Двери нет, вход снят или права нет — не опрашиваем: это не сбой связи.
        if (status === 404 || status === 401 || status === 403) return;
        pauza = 15_000;
      }

      taymer = window.setTimeout(() => void shag(), pauza);
    };

    void shag();
  };

  const z = zamki();
  if (z !== null && kanal !== null) {
    // Замок держится, пока вкладка жива: обещание не исполняется до `ostanovit`.
    void z.request(ZAMOK_VEDUSHCHEY, () => new Promise<void>((gotovo) => {
      if (!zhiva) {
        gotovo();

        return;
      }
      otpustitVedushchuyu = gotovo;
      oprashivat();
    })).catch(() => {});
  } else {
    oprashivat();
  }

  return {
    skazat,
    ostanovit: () => {
      zhiva = false;
      if (taymer !== undefined) window.clearTimeout(taymer);
      otpustitVedushchuyu?.();
      kanal?.close();
    },
  };
}

/**
 * Взять право показать карточку диалога. `true` — показывает эта вкладка; замок держится, пока
 * `derzhat` не исполнится (карточка ушла с экрана). `false` — диалог уже показывает другая вкладка.
 */
export async function vzyatDialog(obrashchenieId: number, derzhat: Promise<void>): Promise<boolean> {
  const z = zamki();
  if (z === null) return true;

  return new Promise<boolean>((otvet) => {
    void z.request(`opsmind-uved-d-${obrashchenieId}`, { ifAvailable: true }, async (zamok) => {
      otvet(zamok !== null);
      if (zamok !== null) await derzhat;
    }).catch(() => otvet(true));
  });
}
